/**
 * HourlySummaryPage — V2 main orchestrator for the Hourly Summary Report.
 *
 * Full report page with: time range selection, report-level filters,
 * summary + detailed views, CSV/PDF export, event notes, reason editing.
 *
 * Follows the AlertDetailsPage.jsx pattern precisely with hourly-specific
 * summary data (key metrics, top 5 residents, hourly breakdown table).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getUsername,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateTimeIso, formatDateTime12Hour, getToday, getWeekRange, getMonthRange, getQuarterRange, convertIsoToLocal } from '../../../libs.jsx';
import { exportAlertDetailsCsv, exportAlertDetailsPdf, exportHourlySummaryCsv, exportHourlySummaryPdf } from '../../../services/csvExport.js';
import {
    fetchHourlySummaryReport,
    fetchFilterOptions,
    fetchPreformNotes,
    addEventNote,
    editEventReason,
} from './api/hourlySummaryApi.js';

import AlertDetailsTable, { COLUMN_DEFS } from '../alert-details/components/AlertDetailsTable.jsx';
import HourlySummaryView from './components/HourlySummaryView.jsx';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import ColumnVisibilityToggle from '../../community/residents/components/ColumnVisibilityToggle.jsx';
import EventNoteDialog from '../alert-details/components/EventNoteDialog.jsx';
import EditReasonDialog from '../alert-details/components/EditReasonDialog.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';

import './hourly-summary-page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIME_RANGES = [
    { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0,0,0,0); const e = new Date(); return [d, e]; } },
    { id: 2, label: 'Yesterday', getRange: () => { const d = getToday(-1); d.setHours(0,0,0,0); const e = getToday(-1); e.setHours(23,59,59,999); return [d, e]; } },
    { id: 3, label: 'This Week', getRange: () => { const [s] = getWeekRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 4, label: 'This Month', getRange: () => { const [s] = getMonthRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 5, label: 'This Quarter', getRange: () => { const [s] = getQuarterRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 6, label: 'Last Week', getRange: () => { const [s, e] = getWeekRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 7, label: 'Last Month', getRange: () => { const [s, e] = getMonthRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 8, label: 'Last Quarter', getRange: () => { const [s, e] = getQuarterRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
];

const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);

function emptyReportFilters() {
    return {
        areas: [],
        areaGroups: [],
        areaTypes: [],
        residents: [],
        residentGroups: [],
        users: [],
        userGroups: [],
        deviceCategories: [],
        devices: [],
        eventTypes: [],
        reasons: [],
        responseTime: { enabled: false, operator: 'lt', value: 60 },
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HourlySummaryPage = ({ run }) => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session]
    );
    const eventNotesActive = Boolean(session?.event_notes_active);
    const eventNotesPreform = Boolean(session?.event_notes_preform);
    const eventNotesFreeform = Boolean(session?.event_notes_freeform);
    const isReasonEditable = Boolean(session?.is_reason_editable);

    // -- Navigation state (from ScheduledReportsPage "Run Report Local") --
    const location = useLocation();
    const navStateRef = useRef(location.state);
    const navState = navStateRef.current;

    // -- Report state --
    const [status, setStatus] = useState('init'); // 'init' | 'loading' | 'ready'
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [hourlyStats, setHourlyStats] = useState({});
    const [overLimit, setOverLimit] = useState(false);
    const [csvUrl, setCsvUrl] = useState('');

    // -- Time range --
    const [rangeId, setRangeId] = useState(navState?.rangeId ?? 2);
    const [rangeName, setRangeName] = useState(() => {
        if (!navState?.rangeId) return 'Yesterday';
        return ({ 1: 'Today', 2: 'Yesterday', 3: 'This Week', 4: 'This Month',
                  5: 'This Quarter', 6: 'Last Week', 7: 'Last Month', 8: 'Last Quarter' })[navState.rangeId] || 'Yesterday';
    });
    const [customRange, setCustomRange] = useState(false);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) return new Date(navState.startDate);
        const d = getToday(-1); d.setHours(0,0,0,0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) return new Date(navState.endDate);
        const d = getToday(-1); d.setHours(23,59,59,999); return d;
    });
    const [startDisplay, setStartDisplay] = useState('');
    const [endDisplay, setEndDisplay] = useState('');

    // -- Report filters (pre-query) — pre-fill from navigation state if present --
    const [reportFilters, setReportFilters] = useState(() => {
        if (navState?.filters) return { ...emptyReportFilters(), ...navState.filters };
        return emptyReportFilters();
    });
    const [filterOptions, setFilterOptions] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- View mode --
    const [viewMode, setViewMode] = useState('summary'); // 'summary' | 'detailed'

    // -- Table state (detailed view) --
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState('event_datetime_local');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();
    // forceInclude surfaces the Notes column ONCE for existing users who already have a
    // saved column preference, then records a "shown" marker so they can still toggle it
    // off if they want. New users get it via DEFAULT_VISIBLE / ALL_COLUMN_KEYS as usual.
    const [visibleColumns, handleToggleColumn] = usePersistedColumns('report-hourly-summary', ALL_COLUMN_KEYS, ALL_COLUMN_KEYS, ['notes']);

    // -- Dialogs --
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteTarget, setNoteTarget] = useState(null);
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [reasonTarget, setReasonTarget] = useState(null);
    const [prenoteData, setPrenoteData] = useState([]);

    // -- UI --
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const autoRunRef = useRef(run);
    const shouldRerunRef = useRef(false);
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);
    const pendingAutoRunRef = useRef(false);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Load filter options + preform notes on mount / campus change
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        fetchFilterOptions(campusId).then(setFilterOptions);
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
        if (eventNotesActive) {
            fetchPreformNotes(campusId).then(setPrenoteData);
        }
    }, [campusId, eventNotesActive]);

    // ======================================================================
    // Auto-run on mount (from `run` prop OR ScheduledReportsPage
    // "Run Report Local" navigation state).
    // ======================================================================
    useEffect(() => {
        const shouldRun = autoRunRef.current || navState?.autoRun;
        if (!shouldRun) return;
        autoRunRef.current = false;
        handleRunReport();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ======================================================================
    // Close time popover on outside click / Escape
    // ======================================================================
    useEffect(() => {
        if (!timePopoverOpen) return;
        const handleClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [timePopoverOpen]);

    // ======================================================================
    // Time range helpers
    // ======================================================================
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterDrawerOpen(true);
    }, [handleCustomToggle]);

    // ======================================================================
    // Run Report
    // ======================================================================
    const handleRunReport = useCallback(async () => {
        setStatus('loading');
        setOverLimit(false);

        // Cap end to now if in future
        const now = new Date();
        const endToUse = end > now ? now : end;

        const stD = formatDateTimeIso(start);
        const enD = formatDateTimeIso(endToUse);

        setStartDisplay(formatDateTime12Hour(start));
        setEndDisplay(formatDateTime12Hour(endToUse));

        // Build filter strings from arrays. Drawer keys reasons by
        // cloud_reason_code_id; combine with each row's matching reason_code_id.
        const cloudReasonIds = reportFilters.reasons || [];
        const reasonIds = cloudReasonIds
            .map((cid) => {
                const rc = (filterOptions.reasonCodes || []).find((r) => String(r.cloud_reason_code_id) === String(cid));
                return rc?.reason_code_id;
            })
            .filter((id) => id != null);
        const combinedReasonIds = [...new Set([...cloudReasonIds, ...reasonIds])];
        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: combinedReasonIds.join(','),
            responseTime: reportFilters.responseTime?.enabled
                ? [true, reportFilters.responseTime.operator, reportFilters.responseTime.value]
                : [false, 'lt', 60],
        };

        const result = await fetchHourlySummaryReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            filters: filterPayload,
        });

        if (result.ErrorCode) {
            const errMap = {
                11: 'Report timed out. Contact your customer success manager for large reports.',
                10: 'An unknown error occurred. Please contact customer success if the problem persists.',
                12: 'Request unauthorized. Service may be unavailable.',
            };
            showToast(errMap[result.ErrorCode] || 'Report request failed.', 'error');
            setStatus('init');
            return;
        }

        if (result.overlimit) {
            setOverLimit(true);
            setCsvUrl(result.url || '');
            showToast('Report exceeds 5,000 events. Details will need to be downloaded.', 'warning');
        } else {
            setOverLimit(false);
            setCsvUrl(result.url || '');
        }

        setReportData(result.records || []);
        setSummaryData(result.summary || []);
        setHourlyStats({
            max: result.max,
            min: result.min,
            avg: result.avg,
            savg: result.savg,
            mostActive: result.mostActive || [],
        });
        setPage(1);
        setStatus('ready');
    }, [start, end, campusId, reportFilters, showToast]);

    // ======================================================================
    // Auto-run after quick time range selection
    // ======================================================================
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        pendingAutoRunRef.current = true;
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    // ======================================================================
    // Re-run report after filter apply / clear (if already run)
    // ======================================================================
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    // ======================================================================
    // Data pipeline: search → column filters → sort → paginate
    // ======================================================================
    const searchedData = useMemo(() => {
        if (!search) return reportData;
        const term = search.toLowerCase();
        return reportData.filter((row) =>
            (row.resident_name || '').toLowerCase().includes(term) ||
            (row.area_name || '').toLowerCase().includes(term) ||
            (row.description || '').toLowerCase().includes(term) ||
            (row.manufacture_uuid || '').toLowerCase().includes(term) ||
            (row.display_name || '').toLowerCase().includes(term)
        );
    }, [reportData, search]);

    const columnFilteredData = useMemo(() => {
        const activeFilters = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
        if (activeFilters.length === 0) return searchedData;
        return searchedData.filter((row) =>
            activeFilters.every(([key, vals]) => {
                if (key === 'event_datetime_local') {
                    // Match by hour label (e.g. "3 PM") — field is already local, no offset needed
                    const local = convertIsoToLocal(row.event_datetime_local);
                    if (!local) return false;
                    const match = local.match(/(\d{1,2}):\d{2}(AM|PM)/i);
                    const hourLabel = match ? `${match[1]} ${match[2].toUpperCase()}` : '';
                    return vals.includes(hourLabel);
                }
                const val = row[key] != null ? String(row[key]) : '';
                return vals.includes(val);
            })
        );
    }, [searchedData, columnFilters]);

    const sortedData = useMemo(() => {
        if (!sortColumn) return columnFilteredData;
        const sorted = [...columnFilteredData].sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';
            if (sortColumn === 'response_time' || sortColumn === 'service_time') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
                return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortOrder === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [columnFilteredData, sortColumn, sortOrder]);

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);
    const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
    const showingEnd = Math.min(safePage * perPage, totalFiltered);

    // Page number buttons (max 5 visible)
    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxButtons = 5;
        let startP = Math.max(1, safePage - Math.floor(maxButtons / 2));
        let endP = Math.min(totalPages, startP + maxButtons - 1);
        if (endP - startP < maxButtons - 1) startP = Math.max(1, endP - maxButtons + 1);
        for (let i = startP; i <= endP; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) {
                setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => {
        setColumnFilters({});
        setPage(1);
    }, []);


    const handleSearchChange = useCallback((e) => {
        setSearch(e.target.value);
        setPage(1);
    }, []);

    const handlePerPageChange = useCallback((e) => {
        setPerPage(parseInt(e.target.value, 10));
        setPage(1);
    }, []);

    const handleCampusChange = useCallback((e) => {
        const newId = parseInt(e.target.value, 10);
        setCampusId(newId);
        updateSessionCampus(newId);
        setReportData([]);
        setSummaryData([]);
        setHourlyStats({});
        setStatus('init');
    }, []);

    // -- Filter drawer --
    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    // Count active report-level filters
    const activeReportFilterCount = useMemo(() => {
        let count = 0;
        Object.keys(reportFilters).forEach((k) => {
            if (k === 'responseTime') {
                if (reportFilters[k]?.enabled) count++;
            } else {
                count += (reportFilters[k]?.length || 0);
            }
        });
        return count;
    }, [reportFilters]);

    // -- Note dialog --
    const handleOpenNoteDialog = useCallback((event) => {
        setNoteTarget(event);
        setNoteDialogOpen(true);
    }, []);

    const handleSaveNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('Note added successfully.');
            setNoteDialogOpen(false);
            setNoteTarget(null);
            handleRunReport();
        } else {
            showToast(result[1] || 'Failed to add note.', 'error');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Reason dialog --
    const handleOpenReasonDialog = useCallback((event) => {
        setReasonTarget(event);
        setReasonDialogOpen(true);
    }, []);

    const handleSaveReason = useCallback(async (eventId, newReasonId, oldReasonId) => {
        setSaving(true);
        const result = await editEventReason(eventId, newReasonId, oldReasonId, campusId, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('Reason updated successfully.');
            setReasonDialogOpen(false);
            setReasonTarget(null);
            handleRunReport();
        } else {
            showToast(result[1] || 'Failed to update reason.', 'error');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Export (detailed) --
    const handleExportCsv = useCallback(() => {
        let maxNotes = 0;
        sortedData.forEach((row) => {
            if (row.notes && row.notes.length > maxNotes) {
                maxNotes = row.notes.length;
            }
        });
        exportAlertDetailsCsv(sortedData, maxNotes, 'hourly-details.csv', visibleColumns);
    }, [sortedData, visibleColumns]);

    const handleExportPdf = useCallback(() => {
        exportAlertDetailsPdf(sortedData, 'Hourly Summary Report — Detailed', `${startDisplay} - ${endDisplay}`, 'hourly-details.pdf', visibleColumns);
    }, [sortedData, startDisplay, endDisplay, visibleColumns]);

    // -- Export (summary) --
    const handleExportSummaryCsv = useCallback(() => {
        exportHourlySummaryCsv(summaryData, 'hourly-summary.csv');
    }, [summaryData]);

    const handleExportSummaryPdf = useCallback(() => {
        exportHourlySummaryPdf(summaryData, 'Hourly Summary Report', `${startDisplay} - ${endDisplay}`, 'hourly-summary.pdf');
    }, [summaryData, startDisplay, endDisplay]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast(result[1] || 'Failed to create schedule.', 'error'); }
    }, [campusId, showToast]);

    // ======================================================================
    // Render helpers
    // ======================================================================
    const reportFilterChips = useMemo(() => {
        const chips = [];
        const labelMap = {
            areas: 'Areas', areaGroups: 'Area Groups', areaTypes: 'Area Types',
            residents: 'Residents', residentGroups: 'Resident Groups',
            users: 'Users', userGroups: 'User Groups',
            deviceCategories: 'Device Categories', devices: 'Devices',
            eventTypes: 'Event Types', reasons: 'Reasons',
        };
        Object.keys(labelMap).forEach((k) => {
            const ids = reportFilters[k] || [];
            if (ids.length > 0) {
                const optKey = k === 'reasons' ? 'reasonCodes' : k;
                const opts = filterOptions[optKey] || [];
                const names = ids.slice(0, 3).map((id) => {
                    const opt = opts.find((o) => {
                        const oId = o.area_id || o.area_group_id || o.area_type_id || o.resident_id ||
                            o.resident_group_id || o.user_id || o.group_id || o.device_category_id ||
                            o.device_id || o.event_type_id || o.reason_code_id;
                        return String(oId) === String(id);
                    });
                    return opt ? (opt.area_name || opt.area_group_name || opt.area_type_name ||
                        opt.resident_name || opt.resident_group_name || opt.display_name ||
                        opt.group_name || opt.device_category_short_name || opt.device_name ||
                        opt.event_type_name || opt.reason_code_description) : id;
                });
                const label = ids.length > 3 ? `${names.join(', ')} +${ids.length - 3} more` : names.join(', ');
                chips.push({ key: k, label: `${labelMap[k]}: ${label}` });
            }
        });
        if (reportFilters.responseTime?.enabled) {
            const opLabel = { lt: '<', gt: '>', eq: '=' };
            chips.push({
                key: 'responseTime',
                label: `Response Time: ${opLabel[reportFilters.responseTime.operator] || '<'} ${reportFilters.responseTime.value} min`,
            });
        }
        return chips;
    }, [reportFilters, filterOptions]);

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">

                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : toast.type === 'warning' ? 'fe-alert-triangle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            {/* Breadcrumb + Campus Selector */}
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Hourly Summary</span>
                                </div>

                                {/* Campus selector */}
                                <div className="ar-campus-row">
                                    <label htmlFor="hs-campus-selector" className="ar-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="hs-campus-selector"
                                            value={campusId}
                                            onChange={handleCampusChange}
                                            className="ar-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            {/* Page title + time summary + actions row */}
                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Hourly Summary</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleDrawerOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeReportFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeReportFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle — pinned between header and scrollable content */}
                    {status === 'ready' && !overLimit ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button
                                    className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`}
                                    onClick={() => setViewMode('summary')}
                                >
                                    Summary
                                </button>
                                <button
                                    className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`}
                                    onClick={() => setViewMode('detailed')}
                                >
                                    Detailed
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">

                    {/* Report filter chips */}
                    {reportFilterChips.length > 0 ? (
                        <div className="ar-filter-chips">
                            {reportFilterChips.map((chip) => (
                                <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                            ))}
                            <button
                                className="ar-filter-chips__clear"
                                onClick={handleClearReportFilters}
                            >
                                Clear All
                            </button>
                        </div>
                    ) : null}

                    {/* Content area */}
                    {status === 'init' ? (
                        <div className="ar-init-message">
                            <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                            <p>Select a time range and click <strong>Run Report</strong> to generate report.</p>
                            <button
                                className="dp-btn dp-btn--primary dp-btn--sm"
                                onClick={handleRunReport}
                                disabled={status === 'loading'}
                                style={{ marginTop: 'var(--space-3)' }}
                            >
                                <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                {status === 'loading' ? 'Running...' : 'Run Report'}
                            </button>
                        </div>
                    ) : null}

                    {status === 'loading' ? (
                        <div className="ar-loading">
                            <div className="ar-loading__spinner" />
                            <p>Loading report data...</p>
                        </div>
                    ) : null}

                    {status === 'ready' && overLimit ? (
                        <div className="ar-overlimit">
                            <i className="fe fe-alert-triangle" style={{ fontSize: '32px', color: 'var(--color-warning)' }} />
                            <h3>Report Too Large</h3>
                            <p>This report contains more than 5,000 events. Summary view is available below.</p>
                            {csvUrl ? (
                                <a href={csvUrl} className="dp-btn dp-btn--primary" target="_blank" rel="noopener noreferrer">
                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                    Download Full Report (CSV)
                                </a>
                            ) : null}
                        </div>
                    ) : null}

                    {status === 'ready' ? (
                        <>
                            {/* Summary view */}
                            {viewMode === 'summary' || overLimit ? (
                                <>
                                    {/* Summary export controls */}
                                    {summaryData.length > 0 ? (
                                        <div className="hs-summary-controls">
                                            <button className="rp-btn rp-btn--secondary" onClick={handleExportSummaryCsv}>
                                                <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                                CSV
                                            </button>
                                            <button className="rp-btn rp-btn--secondary" onClick={handleExportSummaryPdf}>
                                                <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                                PDF
                                            </button>
                                        </div>
                                    ) : null}
                                    <HourlySummaryView
                                        summaryData={summaryData}
                                        hourlyStats={hourlyStats}
                                        startDisplay={startDisplay}
                                        endDisplay={endDisplay}
                                        start={start}
                                        end={end}
                                        reportFilters={reportFilters}
                                    />
                                </>
                            ) : null}

                            {/* Detailed view */}
                            {viewMode === 'detailed' && !overLimit ? (
                                <div className="ar-detail-view">
                                    {/* Controls row */}
                                    <div className="ap-controls">
                                        <div className="ap-controls__left">
                                            <div className="ap-search">
                                                <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                                </svg>
                                                <input
                                                    type="search"
                                                    placeholder="Search events..."
                                                    value={search}
                                                    onChange={handleSearchChange}
                                                    className="ap-search__input"
                                                    aria-label="Search events"
                                                />
                                            </div>
                                        </div>
                                        <div className="ap-controls__right">
                                            <ColumnVisibilityToggle
                                                columns={COLUMN_DEFS}
                                                visibleColumns={visibleColumns}
                                                onToggle={handleToggleColumn}
                                            />
                                            {status === 'ready' && !overLimit ? (
                                                <>
                                                    <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                                        <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                                        CSV
                                                    </button>
                                                    <button className="rp-btn rp-btn--secondary" onClick={handleExportPdf}>
                                                        <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                                        PDF
                                                    </button>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>

                                    <AlertDetailsTable
                                        data={paginatedData}
                                        allData={sortedData}
                                        visibleColumns={visibleColumns}
                                        sortColumn={sortColumn}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        filters={columnFilters}
                                        onFilterChange={handleColumnFilterChange}
                                        onClearFilters={handleClearColumnFilters}
                                        onAddNote={handleOpenNoteDialog}
                                        onEditReason={handleOpenReasonDialog}
                                        eventNotesActive={eventNotesActive}
                                        isReasonEditable={isReasonEditable}
                                        tzOffset={tzOffset}
                                        dateFilterMode="hour"
                                    />

                                    {/* Pagination */}
                                    <div className="ap-pagination">
                                            <p className="ap-pagination__info">
                                                Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                                                <span className="ap-pagination__emphasis">{totalFiltered}</span> events
                                            </p>
                                            <div className="ap-pagination__controls">
                                                <div className="ap-per-page">
                                                    <label htmlFor="hs-per-page" className="ap-per-page__label">Rows per page:</label>
                                                    <select
                                                        id="hs-per-page"
                                                        value={perPage}
                                                        onChange={handlePerPageChange}
                                                        className="ap-per-page__select"
                                                    >
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                        <option value={200}>200</option>
                                                        <option value={300}>300</option>
                                                    </select>
                                                </div>
                                                <nav className="ap-page-nav" aria-label="Events pagination">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                        disabled={safePage <= 1}
                                                        className="ap-page-btn"
                                                        aria-label="Previous page"
                                                    >
                                                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                                        </svg>
                                                    </button>
                                                    {pageNumbers.map((num) => (
                                                        <button
                                                            key={num}
                                                            type="button"
                                                            onClick={() => setPage(num)}
                                                            className={`ap-page-num${num === safePage ? ' is-active' : ''}`}
                                                            aria-label={`Page ${num}`}
                                                            aria-current={num === safePage ? 'page' : undefined}
                                                        >
                                                            {num}
                                                        </button>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                                        disabled={safePage >= totalPages}
                                                        className="ap-page-btn"
                                                        aria-label="Next page"
                                                    >
                                                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                                        </svg>
                                                    </button>
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                            ) : null}
                        </>
                    ) : null}

                    </div>{/* end ar-content */}

                    {/* Filter Drawer */}
                    <ReportFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={status === 'loading'}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={(date) => setStart(date)}
                        onEndChange={(date) => setEnd(date)}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={4}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteTarget ? (
                        <EventNoteDialog
                            event={noteTarget}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteTarget(null); }}
                            onSave={handleSaveNote}
                            saving={saving}
                        />
                    ) : null}

                    {/* Edit Reason Dialog */}
                    {reasonDialogOpen && reasonTarget ? (
                        <EditReasonDialog
                            event={reasonTarget}
                            reasonCodes={filterOptions.reasonCodes || []}
                            onClose={() => { setReasonDialogOpen(false); setReasonTarget(null); }}
                            onSave={handleSaveReason}
                            saving={saving}
                        />
                    ) : null}

                </div>
            </div>
        </>
    );
};

export default HourlySummaryPage;
